/** The `X-Next-Price-Adjustment-Change-At` header: when the next scheduled price change starts or ends. */
export interface NextPricingBoundary {
  nextPriceAdjustmentChangeAt: string | null
}

interface PricingBoundaryOptions {
  shape?: 'array' | 'object'
}

/**
 * Attaches the next price change time to a pricing-sensitive payload so queries can refetch when
 * prices change. A missing array payload becomes `[]`; a missing object payload becomes `{}`.
 * Axios types header values loosely, so anything but a non-empty string reads as no boundary.
 */
export function attachNextPricingBoundary<TItem>(
  value: TItem[] | null | undefined,
  nextPriceAdjustmentChangeAt: unknown,
  options: { shape: 'array' }
): TItem[] & NextPricingBoundary
export function attachNextPricingBoundary(
  value: null | undefined,
  nextPriceAdjustmentChangeAt: unknown,
  options?: { shape?: 'object' }
): NextPricingBoundary
export function attachNextPricingBoundary<TValue extends object>(
  value: TValue,
  nextPriceAdjustmentChangeAt: unknown,
  options?: { shape?: 'object' }
): TValue & NextPricingBoundary
export function attachNextPricingBoundary(
  value: unknown,
  nextPriceAdjustmentChangeAt: unknown,
  { shape = 'object' }: PricingBoundaryOptions = {}
): NextPricingBoundary {
  const normalizedValue: object =
    shape === 'array'
      ? Array.isArray(value)
        ? value
        : []
      : value && typeof value === 'object' && !Array.isArray(value)
        ? { ...value }
        : {}

  return Object.assign(normalizedValue, {
    nextPriceAdjustmentChangeAt:
      typeof nextPriceAdjustmentChangeAt === 'string' && nextPriceAdjustmentChangeAt
        ? nextPriceAdjustmentChangeAt
        : null,
  })
}
